/**
 * SPH Ortak WGSL Shader Tanimlari
 *
 * Bu dosya tum SPH solver'larin (DFSPH, PBF, PCISPH) ortak kullandigi
 * WGSL shader kodlarini TypeScript string sabitleri olarak export eder.
 *
 * SADECE shader kod stringleri ve yardimci fonksiyonlar icin kullanilir.
 * GPU API cagrilari YOKTUR.
 */

/**
 * SPH Struct Tanimlari
 * Tum SPH solver'larin kullandigi ortak struct'lar
 */
export const SPH_STRUCT_TANIMLARI = `
// ====== SPH UNIFORM PARAMETERS ======
// Total size: 224 bytes (14 * 16-byte alignment)
struct SPHParams {
  // Simulation parameters (16 bytes)
  gravity_x: f32,
  gravity_y: f32,
  gravity_z: f32,
  dt: f32,

  // Physical properties (16 bytes)
  restDensity: f32,
  viscosity: f32,
  surfaceTension: f32,
  boundaryDamping: f32,

  // SPH kernel parameters (16 bytes)
  smoothingRadius: f32,      // h - SPH kernel radius
  particleRadius: f32,       // visual particle radius
  maxParticle: u32,
  activeCount: u32,

  // Kernel precomputed constants (16 bytes)
  poly6Const: f32,           // 315 / (64 * PI * h^9)
  spikyGradConst: f32,       // -45 / (PI * h^6)
  viscLapConst: f32,         // 45 / (PI * h^6)
  cohesionConst: f32,        // 32 / (PI * h^9)

  // Spatial grid parameters (16 bytes)
  gridOrigin_x: f32,
  gridOrigin_y: f32,
  gridOrigin_z: f32,
  gridCellSize: f32,

  // Grid dimensions (16 bytes)
  gridDim_x: u32,
  gridDim_y: u32,
  gridDim_z: u32,
  totalGridCells: u32,

  // Element counts (16 bytes)
  girisAdedi: u32,
  delikAdedi: u32,
  besleyiciAdedi: u32,
  filtreAdedi: u32,

  // Element counts continued (16 bytes)
  engelAdedi: u32,
  cikisAdedi: u32,
  sensorAdedi: u32,
  termalAdedi: u32,

  // Simulation flags (16 bytes)
  termalAktif: u32,
  gazAktif: u32,
  heatmapTipi: u32,
  simZaman: f32,

  // Solver configuration (16 bytes)
  solverTipi: u32,           // 0=DFSPH, 1=PBF, 2=PCISPH
  cflFactor: f32,
  havaBasinci: f32,
  simHizi: f32,

  // DFSPH parameters (16 bytes)
  dfsph_divFreeIter: u32,
  dfsph_constDensIter: u32,
  dfsph_densityError: f32,
  kernelTipi: u32,              // 0=Spiky/Poly6 (default), 1=Wendland C2

  // PBF parameters (16 bytes)
  pbf_iter: u32,
  pbf_epsilon: f32,
  pbf_scorr_k: f32,
  pbf_scorr_n: f32,

  // PBF + PCISPH parameters (16 bytes)
  pbf_xsphC: f32,
  pcisph_iter: u32,
  pcisph_minIter: u32,
  pcisph_maxError: f32,

  // Render parameters (16 bytes)
  renderModu: u32,
  particleBoyutu: f32,
  _pad0: f32,
  _pad1: f32,
};

// ====== PARTICLE DATA STRUCTURES ======
// Particles use Structure-of-Arrays layout in separate buffers:
// Buffer 0: positionMass - vec4(x, y, z, inverseMass)
// Buffer 1: velocityAge - vec4(vx, vy, vz, age)
// Buffer 2: densityMisc - vec4(density, pressure, colorField, sourceId)
// Buffer 3: forceVmax - vec4(fx, fy, fz, vMax)

// ====== DOKUM GIRISI (INLET) ======
// Total size: 128 bytes (8 * 16-byte alignment)
struct DokumGirisi {
  // Position and radius (16 bytes)
  poz_x: f32,
  poz_y: f32,
  poz_z: f32,
  yaricap: f32,

  // Direction and speed (16 bytes)
  yon_x: f32,
  yon_y: f32,
  yon_z: f32,
  hiz: f32,

  // Flow parameters (16 bytes)
  debi: f32,
  maxMesafe: f32,
  maxOmur: f32,
  aktif: u32,

  // Material and section (16 bytes)
  malzemeId: u32,
  kesitTip: u32,
  kesitW: f32,
  kesitH: f32,

  // Thermal and flow profile (16 bytes)
  sicaklik: f32,
  turbulans: f32,
  akisProfil: u32,
  zaman: f32,

  // Ramp and pulse (16 bytes)
  rampaYukselis: f32,
  pulsePer: f32,
  pulseDuty: f32,
  renk: f32,

  // Time control (16 bytes)
  akisBaslangic: f32,
  akisBitis: f32,
  _pad0: f32,
  _pad1: f32,

  // Reserved for future use (16 bytes)
  _reserved0: f32,
  _reserved1: f32,
  _reserved2: f32,
  _reserved3: f32,
};

// ====== HAVA DELIGI (VENT) ======
// Total size: 64 bytes (4 * 16-byte alignment)
struct HavaDeligi {
  // Position and diameter (16 bytes)
  poz_x: f32,
  poz_y: f32,
  poz_z: f32,
  cap: f32,

  // Direction and depth (16 bytes)
  yon_x: f32,
  yon_y: f32,
  yon_z: f32,
  derinlik: f32,

  // Flow parameters (16 bytes)
  geriBasinc: f32,
  akisDirenci: f32,
  gecirgenlik: f32,
  aktif: u32,

  // Type and timing (16 bytes)
  tipi: u32,
  acilmaZ: f32,
  kapanmaZ: f32,
  _pad: f32,
};

// ====== ENGEL (OBSTACLE) ======
// Total size: 128 bytes (8 * 16-byte alignment)
struct Engel {
  // Type and padding (16 bytes)
  tipi: u32,
  _engel_pad0: f32,
  _engel_pad1: f32,
  _engel_pad2: f32,

  // Position and thickness (16 bytes)
  poz_x: f32,
  poz_y: f32,
  poz_z: f32,
  kalinlik: f32,

  // Direction and permeability (16 bytes)
  yon_x: f32,
  yon_y: f32,
  yon_z: f32,
  gecirgenlik: f32,

  // Size and drag (16 bytes)
  boyut_w: f32,
  boyut_h: f32,
  surukleme: f32,
  hareketli: u32,

  // Motion parameters (16 bytes)
  donusHizi: f32,
  salGenlik: f32,
  salFrekans: f32,
  aktif: u32,

  // Reserved padding (16 bytes * 3)
  _epad0: f32, _epad1: f32, _epad2: f32, _epad3: f32,
  _epad4: f32, _epad5: f32, _epad6: f32, _epad7: f32,
  _epad8: f32, _epad9: f32, _epad10: f32, _epad11: f32,
};

// ====== FILTRE (FILTER) ======
// Total size: 64 bytes (4 * 16-byte alignment)
struct Filtre {
  // Position (16 bytes)
  poz_x: f32,
  poz_y: f32,
  poz_z: f32,
  _fpad0: f32,

  // Direction and permeability (16 bytes)
  yon_x: f32,
  yon_y: f32,
  yon_z: f32,
  gecirgenlik: f32,

  // Size and pore (16 bytes)
  boyut_w: f32,
  boyut_h: f32,
  gozenekBoyutu: f32,
  basincDususu: f32,

  // Capacity (16 bytes)
  kapasite: f32,
  aktif: u32,
  _fpad1: f32,
  _fpad2: f32,
};

// ====== CIKIS (OUTLET) ======
// Total size: 64 bytes (4 * 16-byte alignment)
struct Cikis {
  // Position and diameter (16 bytes)
  poz_x: f32,
  poz_y: f32,
  poz_z: f32,
  cap: f32,

  // Direction and type (16 bytes)
  yon_x: f32,
  yon_y: f32,
  yon_z: f32,
  tipi: u32,

  // Control parameters (16 bytes)
  hedefBasinc: f32,
  hedefSeviye: f32,
  aktif: u32,
  acilmaZ: f32,

  // Reserved padding (16 bytes)
  _cpad0: f32,
  _cpad1: f32,
  _cpad2: f32,
  _cpad3: f32,
};

// ====== SENSOR ======
// Total size: 32 bytes (2 * 16-byte alignment)
struct Sensor {
  // Position and radius (16 bytes)
  poz_x: f32,
  poz_y: f32,
  poz_z: f32,
  olcumYaricap: f32,

  // Type and active (16 bytes)
  tipi: u32,
  aktif: u32,
  _spad0: f32,
  _spad1: f32,
};

// ====== SENSOR SONUC (SENSOR RESULT) ======
// Total size: 16 bytes
struct SensorSonuc {
  deger0: f32,
  deger1: f32,
  deger2: f32,
  deger3: f32,
};

// ====== SDF INFO ======
// Total size: 64 bytes (4 * 16-byte alignment)
struct SDFInfo {
  // Origin and cell size (16 bytes)
  origin_x: f32,
  origin_y: f32,
  origin_z: f32,
  cellSize: f32,

  // Dimensions (16 bytes)
  dim_x: u32,
  dim_y: u32,
  dim_z: u32,
  totalCells: u32,

  // Reserved padding (16 bytes * 2)
  _sdfpad0: f32, _sdfpad1: f32, _sdfpad2: f32, _sdfpad3: f32,
  _sdfpad4: f32, _sdfpad5: f32, _sdfpad6: f32, _sdfpad7: f32,
};
`;

/**
 * SPH Kernel Fonksiyonlari
 * Standard SPH smoothing kernel'lari ve yardimci fonksiyonlar
 */
export const SPH_KERNEL_FONKSIYONLARI = `
// ====== SPH KERNEL FUNCTIONS ======

// Poly6 kernel (density estimation)
// W(r,h) = (315 / (64*PI*h^9)) * (h^2 - r^2)^3 for r < h
fn kernelPoly6(r2: f32, h: f32, poly6Const: f32) -> f32 {
  let h2 = h * h;
  if (r2 >= h2) {
    return 0.0;
  }
  let diff = h2 - r2;
  return poly6Const * diff * diff * diff;
}

// Spiky kernel gradient (pressure force)
// grad_W(r,h) = (-45 / (PI*h^6)) * (h - |r|)^2 * (r / |r|)
fn kernelSpikyGrad(r: vec3<f32>, dist: f32, h: f32, spikyConst: f32) -> vec3<f32> {
  if (dist >= h || dist < 0.0001) {
    return vec3<f32>(0.0);
  }
  let diff = h - dist;
  let coeff = spikyConst * diff * diff / dist;
  return r * coeff;
}

// Viscosity kernel Laplacian
// lap_W(r,h) = (45 / (PI*h^6)) * (h - |r|)
fn kernelViscLap(dist: f32, h: f32, viscConst: f32) -> f32 {
  if (dist >= h) {
    return 0.0;
  }
  return viscConst * (h - dist);
}

// Akinci cohesion kernel (surface tension)
// C(r,h) = (32 / (PI*h^9)) * (h-r)^3 * r^3 for r > h/2
// C(r,h) = (32 / (PI*h^9)) * (2*(h-r)^3*r^3 - h^6/64) for r <= h/2
fn kernelCohesion(dist: f32, h: f32, cohesionConst: f32) -> f32 {
  if (dist >= h || dist < 0.0001) {
    return 0.0;
  }

  let halfH = h * 0.5;
  if (dist > halfH) {
    let diff = h - dist;
    return cohesionConst * diff * diff * diff * dist * dist * dist;
  } else {
    let diff = h - dist;
    let val = diff * diff * diff * dist * dist * dist;
    let h6 = h * h * h * h * h * h;
    let inner = h6 * 0.015625; // h^6 / 64
    return cohesionConst * (2.0 * val - inner);
  }
}

// Wendland C2 kernel (compact support q < 2, smoother than Poly6)
// W(r,h) = alpha * (1 - q/2)^4 * (2q + 1), q = r/h
fn kernelWendlandC2(dist: f32, h: f32) -> f32 {
  let q = dist / h;
  if (q >= 2.0) { return 0.0; }
  let alpha = 21.0 / (16.0 * 3.14159265 * h * h * h);
  let t = 1.0 - q * 0.5;
  return alpha * t * t * t * t * (2.0 * q + 1.0);
}

// Wendland C2 gradient
fn kernelWendlandC2Grad(r: vec3<f32>, dist: f32, h: f32) -> vec3<f32> {
  let q = dist / h;
  if (q >= 2.0 || dist < 1e-6) { return vec3<f32>(0.0); }
  let alpha = 21.0 / (16.0 * 3.14159265 * h * h * h);
  let t = 1.0 - q * 0.5;
  let gradMag = alpha * (-5.0 * q * t * t * t) / (h * h);
  return r * (gradMag / dist);
}

// Tensile instability correction (Monaghan)
// Prevents particle clumping under negative pressure
// W_ref = kernel value at reference distance (particle spacing ~ h*0.5)
fn tensileCorrection(W_ij: f32, W_ref: f32) -> f32 {
  let ratio = W_ij / max(W_ref, 1e-10);
  return -0.2 * pow(ratio, 4.0);
}

// Precompute W_ref for tensile correction (call once per frame)
fn wendlandRefValue(h: f32) -> f32 {
  let deltaP = h * 0.5;
  return kernelWendlandC2(deltaP, h);
}

// Distance squared helper
fn distSq(a: vec3<f32>, b: vec3<f32>) -> f32 {
  let d = a - b;
  return dot(d, d);
}

// Safe normalize (returns (0,1,0) if length is near zero)
fn safeNormalize(v: vec3<f32>) -> vec3<f32> {
  let len = length(v);
  if (len < 0.0001) {
    return vec3<f32>(0.0, 1.0, 0.0);
  }
  return v / len;
}
`;

/**
 * Spatial Grid Yardimci Fonksiyonlar
 * Uniform grid bazli neighbor search icin
 */
export const SPH_GRID_YARDIMCI = `
// ====== SPATIAL GRID HELPERS ======

// World position -> grid cell index (3D)
fn posToCell(pos: vec3<f32>, origin: vec3<f32>, cellSize: f32) -> vec3<i32> {
  return vec3<i32>(floor((pos - origin) / cellSize));
}

// 3D grid cell -> 1D flat index
fn cellToIndex(cell: vec3<i32>, gridDim: vec3<u32>) -> u32 {
  let c = vec3<u32>(
    clamp(u32(cell.x), 0u, gridDim.x - 1u),
    clamp(u32(cell.y), 0u, gridDim.y - 1u),
    clamp(u32(cell.z), 0u, gridDim.z - 1u)
  );
  return c.x + c.y * gridDim.x + c.z * gridDim.x * gridDim.y;
}

// Hash function for cell (Z-curve for better cache locality)
fn cellHash(cell: vec3<i32>, gridDim: vec3<u32>) -> u32 {
  // Clamp to valid range first
  let cx = clamp(u32(cell.x), 0u, gridDim.x - 1u);
  let cy = clamp(u32(cell.y), 0u, gridDim.y - 1u);
  let cz = clamp(u32(cell.z), 0u, gridDim.z - 1u);

  // Morton code (Z-curve) - interleave bits for spatial locality
  // For simplicity, we use linear index here
  return cx + cy * gridDim.x + cz * gridDim.x * gridDim.y;
}

// Check if cell is within grid bounds
fn cellInBounds(cell: vec3<i32>, gridDim: vec3<u32>) -> bool {
  return cell.x >= 0 && cell.x < i32(gridDim.x) &&
         cell.y >= 0 && cell.y < i32(gridDim.y) &&
         cell.z >= 0 && cell.z < i32(gridDim.z);
}

// Get neighbor cell offset index (3x3x3 = 27 neighbors including self)
fn getNeighborOffset(idx: u32) -> vec3<i32> {
  let i = i32(idx);
  let z = i / 9;
  let y = (i % 9) / 3;
  let x = i % 3;
  return vec3<i32>(x - 1, y - 1, z - 1);
}
`;

/**
 * SDF Collision Yardimci Fonksiyonlar
 * Signed Distance Field bazli carpismalar icin
 */
export const SPH_SDF_YARDIMCI = `
// ====== SDF COLLISION HELPERS ======

// Sample SDF value at world position using trilinear interpolation
// Note: In actual shader, sdfData will be bound as storage buffer
fn sdfSampleTrilinear(
  pos: vec3<f32>,
  origin: vec3<f32>,
  cellSize: f32,
  dims: vec3<u32>,
  sdfData: ptr<storage, array<f32>, read>
) -> f32 {
  // World to local grid coordinates
  let local = (pos - origin) / cellSize;
  let cell = vec3<i32>(floor(local));
  let frac = local - vec3<f32>(cell);

  let dimsi = vec3<i32>(i32(dims.x), i32(dims.y), i32(dims.z));

  // Out of bounds check
  if (cell.x < 0 || cell.y < 0 || cell.z < 0 ||
      cell.x >= dimsi.x - 1 || cell.y >= dimsi.y - 1 || cell.z >= dimsi.z - 1) {
    return 1.0; // Outside SDF = positive distance (no collision)
  }

  // 8 corner indices for trilinear interpolation
  let baseIdx = u32(cell.x + cell.y * dimsi.x + cell.z * dimsi.x * dimsi.y);
  let sx = 1u;
  let sy = u32(dimsi.x);
  let sz = u32(dimsi.x * dimsi.y);

  let c000 = (*sdfData)[baseIdx];
  let c100 = (*sdfData)[baseIdx + sx];
  let c010 = (*sdfData)[baseIdx + sy];
  let c110 = (*sdfData)[baseIdx + sx + sy];
  let c001 = (*sdfData)[baseIdx + sz];
  let c101 = (*sdfData)[baseIdx + sx + sz];
  let c011 = (*sdfData)[baseIdx + sy + sz];
  let c111 = (*sdfData)[baseIdx + sx + sy + sz];

  // Trilinear interpolation
  let c00 = mix(c000, c100, frac.x);
  let c10 = mix(c010, c110, frac.x);
  let c01 = mix(c001, c101, frac.x);
  let c11 = mix(c011, c111, frac.x);
  let c0 = mix(c00, c10, frac.y);
  let c1 = mix(c01, c11, frac.y);

  return mix(c0, c1, frac.z);
}

// Compute SDF gradient (normal) using central differences
fn sdfGradient(
  pos: vec3<f32>,
  origin: vec3<f32>,
  cellSize: f32,
  dims: vec3<u32>,
  sdfData: ptr<storage, array<f32>, read>
) -> vec3<f32> {
  let eps = cellSize * 0.5;

  let dx = sdfSampleTrilinear(pos + vec3<f32>(eps, 0.0, 0.0), origin, cellSize, dims, sdfData)
         - sdfSampleTrilinear(pos - vec3<f32>(eps, 0.0, 0.0), origin, cellSize, dims, sdfData);

  let dy = sdfSampleTrilinear(pos + vec3<f32>(0.0, eps, 0.0), origin, cellSize, dims, sdfData)
         - sdfSampleTrilinear(pos - vec3<f32>(0.0, eps, 0.0), origin, cellSize, dims, sdfData);

  let dz = sdfSampleTrilinear(pos + vec3<f32>(0.0, 0.0, eps), origin, cellSize, dims, sdfData)
         - sdfSampleTrilinear(pos - vec3<f32>(0.0, 0.0, eps), origin, cellSize, dims, sdfData);

  let grad = vec3<f32>(dx, dy, dz);
  let len = length(grad);

  if (len < 0.0001) {
    return vec3<f32>(0.0, 1.0, 0.0); // Default up normal
  }

  return grad / len;
}

// Simple box SDF (for debugging)
fn sdfBox(p: vec3<f32>, center: vec3<f32>, halfSize: vec3<f32>) -> f32 {
  let d = abs(p - center) - halfSize;
  return length(max(d, vec3<f32>(0.0))) + min(max(d.x, max(d.y, d.z)), 0.0);
}

// Sphere SDF (for debugging)
fn sdfSphere(p: vec3<f32>, center: vec3<f32>, radius: f32) -> f32 {
  return length(p - center) - radius;
}

// Plane SDF
fn sdfPlane(p: vec3<f32>, normal: vec3<f32>, dist: f32) -> f32 {
  return dot(p, normal) - dist;
}
`;

/**
 * Ortak Binding Group Layout Dokumantasyonu
 * Actual WGSL bindings her shader'da ayri yazilacak
 */
export const SPH_BIND_GROUP_LAYOUT_DOC = `
/**
 * STANDARD BIND GROUP LAYOUT FOR ALL SPH SHADERS
 *
 * @group(0) - Global parameters (uniform)
 *   @binding(0) - SPHParams uniform
 *
 * @group(1) - Particle data (storage, read_write)
 *   @binding(0) - positionMass: array<vec4<f32>>
 *   @binding(1) - velocityAge: array<vec4<f32>>
 *   @binding(2) - densityMisc: array<vec4<f32>>
 *   @binding(3) - forceVmax: array<vec4<f32>>
 *
 * @group(2) - Spatial grid data (storage, read_write)
 *   @binding(0) - cellCount: array<atomic<u32>> (read_write)
 *   @binding(1) - prefixSum: array<u32> (read_write)
 *   @binding(2) - sortedIndex: array<u32> (read_write)
 *   @binding(3) - cellHash: array<u32> (read_write)
 *   @binding(4) - cellOffset: array<atomic<u32>> (read_write)
 *   @binding(5) - blockSums: array<u32> (read_write)
 *
 * @group(3) - Solver-specific data (varies by shader)
 *   Example for DFSPH:
 *     @binding(0) - alpha: array<f32>
 *     @binding(1) - densityAdv: array<f32>
 *   Example for PBF:
 *     @binding(0) - lambda: array<f32>
 *     @binding(1) - deltaPos: array<vec4<f32>>
 *
 * @group(4) - Optional: SDF/obstacles (storage, read)
 *   @binding(0) - sdfInfo: uniform<SDFInfo>
 *   @binding(1) - sdfData: array<f32>
 *
 * @group(5) - Optional: Inlets/outlets/sensors (storage, read)
 *   @binding(0) - dokumGirisleri: array<DokumGirisi>
 *   @binding(1) - havaDelikleri: array<HavaDeligi>
 *   @binding(2) - engeller: array<Engel>
 *   @binding(3) - filtreler: array<Filtre>
 *   @binding(4) - cikislar: array<Cikis>
 *   @binding(5) - sensorler: array<Sensor>
 *   @binding(6) - sensorSonuclar: array<SensorSonuc> (read_write)
 */
`;

/**
 * Ortak Binding Tanimlari Template
 * Her shader bu template'i kullanabilir
 */
export const SPH_ORTAK_BINDING_TANIMLARI = `
// ====== COMMON BINDINGS (Groups 0-2) ======
// Must match FikoSPHGPUHesap.olusturBindGroupLayoutlar() exactly

@group(0) @binding(0) var<uniform> params: SPHParams;

@group(1) @binding(0) var<storage, read_write> positionMass: array<vec4<f32>>;
@group(1) @binding(1) var<storage, read_write> velocityAge: array<vec4<f32>>;
@group(1) @binding(2) var<storage, read_write> densityMisc: array<vec4<f32>>;
@group(1) @binding(3) var<storage, read_write> forceVmax: array<vec4<f32>>;
@group(1) @binding(4) var<storage, read_write> atomicsBuffer: array<u32>;
@group(1) @binding(5) var<storage, read_write> heatmapBuffer: array<f32>;

@group(2) @binding(0) var<storage, read_write> cellCount: array<atomic<u32>>;
@group(2) @binding(1) var<storage, read_write> prefixSum: array<u32>;
@group(2) @binding(2) var<storage, read_write> sortedIndex: array<u32>;
@group(2) @binding(3) var<storage, read_write> cellHashBuffer: array<u32>;
@group(2) @binding(4) var<storage, read_write> cellOffset: array<atomic<u32>>;
@group(2) @binding(5) var<storage, read_write> blockSums: array<u32>;
`;

/**
 * Yardimci Fonksiyon: Shader Birlestirici
 * Birden fazla WGSL kod parcasini birlestirir
 */
export function buildShader(...sections: string[]): string {
  const combined = sections.join('\n\n');
  return deduplicateDeclarations(combined);
}

function deduplicateDeclarations(code: string): string {
  const seenStructs = new Set<string>();
  let result = code.replace(/^struct\s+(\w+)\s*\{[\s\S]*?\};?/gm, (match, name) => {
    if (seenStructs.has(name)) return `// [deduplicated] struct ${name}`;
    seenStructs.add(name);
    return match;
  });
  const seenBindings = new Set<string>();
  result = result.replace(/^@group\((\d+)\)\s*@binding\((\d+)\)\s+var\b.*$/gm, (match, g, b) => {
    const key = `g${g}b${b}`;
    if (seenBindings.has(key)) return `// [deduplicated] ${match.trim()}`;
    seenBindings.add(key);
    return match;
  });
  return result;
}

/**
 * Tum ortak shader kodlarini tek bir string'de doner
 * Kolaylik icin kullanilabilir
 */
export function getFullCommonShaderCode(): string {
  return buildShader(
    SPH_STRUCT_TANIMLARI,
    SPH_KERNEL_FONKSIYONLARI,
    SPH_GRID_YARDIMCI,
    SPH_SDF_YARDIMCI
  );
}

/**
 * Ortak bindings ile birlikte tam shader kodu
 */
export function buildShaderWithCommonBindings(...additionalSections: string[]): string {
  return buildShader(
    SPH_STRUCT_TANIMLARI,
    SPH_KERNEL_FONKSIYONLARI,
    SPH_GRID_YARDIMCI,
    SPH_SDF_YARDIMCI,
    SPH_ORTAK_BINDING_TANIMLARI,
    ...additionalSections
  );
}

/**
 * Export all constants for individual use
 */
export const SPH_ORTAK_SHADERS = {
  STRUCT_TANIMLARI: SPH_STRUCT_TANIMLARI,
  KERNEL_FONKSIYONLARI: SPH_KERNEL_FONKSIYONLARI,
  GRID_YARDIMCI: SPH_GRID_YARDIMCI,
  SDF_YARDIMCI: SPH_SDF_YARDIMCI,
  BIND_GROUP_LAYOUT_DOC: SPH_BIND_GROUP_LAYOUT_DOC,
  ORTAK_BINDING_TANIMLARI: SPH_ORTAK_BINDING_TANIMLARI,
  buildShader,
  getFullCommonShaderCode,
  buildShaderWithCommonBindings,
};

/**
 * Default export for convenience
 */
export default SPH_ORTAK_SHADERS;
